import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Database, Zap, Shield, CheckCircle, ArrowRight, Search, Globe, RefreshCw, Users, BarChart3, Target, Building, Mail, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const CRMEnrichmentAgent = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 CRM Enrichment Agent",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "AI-powered CRM enrichment agent that automatically fills missing contact and company data from 15+ sources. Keeps your Salesforce, HubSpot, and Pipedrive records accurate and complete.",
      "url": "https://noq5.ai/crm-enrichment-agent",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is a CRM enrichment agent?", "acceptedAnswer": { "@type": "Answer", "text": "A CRM enrichment agent is an AI system that automatically fills missing contact and company data in your CRM — including job titles, company size, industry, tech stack, funding status, and social profiles — by aggregating data from multiple sources in real-time." } },
        { "@type": "Question", "name": "How does CRM enrichment differ from tools like Apollo or ZoomInfo?", "acceptedAnswer": { "@type": "Answer", "text": "Traditional enrichment tools provide static database lookups. A CRM enrichment agent uses AI to aggregate from 15+ sources, prioritize enrichment for active deals, maintain freshness automatically, and integrate enrichment into the full CRM update workflow — not just contact data." } },
        { "@type": "Question", "name": "What data fields does the enrichment agent populate?", "acceptedAnswer": { "@type": "Answer", "text": "The agent enriches 30+ fields including job title, department, seniority level, company size, industry, revenue estimate, tech stack, recent funding, hiring signals, LinkedIn URL, phone, verified email, company description, and headquarters location." } },
        { "@type": "Question", "name": "Is enriched data GDPR compliant?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. NoQ5 only enriches from publicly available and licensed data sources. All enrichment respects GDPR consent requirements, supports right-to-deletion requests, and processes data in EU data centers." } }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "CRM Agent", "item": "https://noq5.ai/crm-agent" },
        { "@type": "ListItem", "position": 3, "name": "CRM Enrichment Agent", "item": "https://noq5.ai/crm-enrichment-agent" }
      ]
    }
  ];

  const enrichmentFields = [
    { category: "Contact Data", fields: ["Job Title", "Department", "Seniority Level", "Phone (Direct)", "Email (Verified)", "LinkedIn URL", "Twitter/X Handle", "Location"] },
    { category: "Company Data", fields: ["Company Size", "Industry", "Revenue Estimate", "Founded Year", "Headquarters", "Company Description", "Website", "Logo URL"] },
    { category: "Intelligence Data", fields: ["Tech Stack", "Recent Funding", "Funding Stage", "Hiring Signals", "Recent News", "Competitor Usage", "Growth Rate", "Employee Count Trend"] },
    { category: "Sales Context", fields: ["Decision Maker Score", "ICP Fit Score", "Persona Type", "Buying Stage Signal", "Account Tier", "Engagement History", "Previous Interactions", "Referral Source"] },
  ];

  const beforeAfter = [
    { before: "30-50% of CRM contact records missing job title, phone, or company data", after: "95%+ field completeness across all contact and company records" },
    { before: "Manual research: 15-30 minutes per contact on LinkedIn, company sites, databases", after: "AI enrichment agent fills 30+ fields in under 5 seconds per contact" },
    { before: "Enrichment data goes stale — job changes, company pivots, funding events missed", after: "Continuous re-enrichment triggers when contacts change roles or companies raise funding" },
    { before: "Paying $15,000-50,000/year for ZoomInfo or Apollo credits that expire", after: "Enrichment included in your CRM agent — no separate tool, credits, or contract" },
  ];

  return (
    <Layout>
      <SEOHead
        title="CRM Enrichment Agent: Auto-Fill 30+ Fields in Salesforce, HubSpot & Pipedrive"
        description="Stop paying for ZoomInfo credits. NoQ5's CRM enrichment agent auto-fills 30+ contact and company fields in Salesforce, HubSpot and Pipedrive: job title, tech stack, funding, headcount and more. GDPR, EU-hosted."
        keywords="CRM enrichment agent, CRM data enrichment, AI CRM enrichment, contact enrichment automation, company data enrichment, salesforce enrichment, hubspot enrichment, pipedrive enrichment, B2B data enrichment, lead enrichment agent, ZoomInfo alternative, Apollo alternative, automated CRM enrichment"
        canonicalUrl="https://noq5.ai/crm-enrichment-agent"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/crm-agent" className="hover:text-primary">CRM Agent</Link> / <span className="text-foreground">CRM Enrichment Agent</span>
            </nav>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Database className="h-4 w-4" />
              <span>CRM Enrichment Agent</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">CRM Enrichment Agent</span>: Auto-Fill Every Missing Field
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Stop wasting hours researching contacts manually. NoQ5's <strong>CRM enrichment agent</strong> automatically fills missing job titles, company data, tech stack, funding status, and 30+ fields from 15+ data sources — directly into your Salesforce, HubSpot, or Pipedrive.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trial">
                <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                  Try CRM Enrichment Agent <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/crm-agent">
                <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-border text-foreground hover:bg-accent">
                  See Full CRM Agent
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl md:text-4xl font-bold text-primary mb-1">30+</div><div className="text-sm text-muted-foreground">Fields Enriched Per Contact</div></div>
            <div><div className="text-3xl md:text-4xl font-bold text-primary mb-1">15+</div><div className="text-sm text-muted-foreground">Data Sources Aggregated</div></div>
            <div><div className="text-3xl md:text-4xl font-bold text-primary mb-1">95%</div><div className="text-sm text-muted-foreground">Field Accuracy Rate</div></div>
            <div><div className="text-3xl md:text-4xl font-bold text-primary mb-1">&lt;5s</div><div className="text-sm text-muted-foreground">Per Contact Enrichment</div></div>
          </div>
        </div>
      </section>

      {/* Before / After */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
            Without vs. With a CRM Enrichment Agent
          </h2>
          <div className="space-y-4">
            {beforeAfter.map((item, i) => (
              <div key={i} className="grid md:grid-cols-2 gap-4">
                <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-5 flex items-start gap-3">
                  <Search className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-semibold text-destructive uppercase mb-1">Manual Research</div>
                    <p className="text-sm text-foreground">{item.before}</p>
                  </div>
                </div>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-semibold text-primary uppercase mb-1">AI Enrichment Agent</div>
                    <p className="text-sm text-foreground">{item.after}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is a CRM Enrichment Agent */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
            What Is a CRM Enrichment Agent?
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <p>
              A <strong>CRM enrichment agent</strong> is a specialized AI system that automatically identifies and fills missing data in your CRM records. Unlike traditional enrichment tools that rely on static databases and manual lookups, a CRM enrichment agent uses AI to aggregate data from 15+ sources in real-time, cross-reference for accuracy, and continuously refresh records as information changes.
            </p>
            <p>
              The critical difference between a <strong>CRM enrichment agent</strong> and point tools like Apollo, ZoomInfo, or Clearbit is scope and intelligence. Point tools give you a database to search. An enrichment agent is embedded in your CRM workflow — it knows which contacts are in active deal cycles, prioritizes enrichment for high-value prospects, and triggers re-enrichment when signals change (job change, funding round, company acquisition).
            </p>
            <h3 className="text-2xl font-bold text-foreground">How CRM Enrichment Works With NoQ5</h3>
            <ol className="list-decimal pl-6 space-y-3">
              <li><strong>Gap Detection</strong> — The agent scans your CRM and identifies records with missing or outdated fields (e.g., contacts without phone numbers, companies without industry classification).</li>
              <li><strong>Multi-Source Aggregation</strong> — For each gap, the agent queries 15+ data sources — public records, company websites, social profiles, news APIs, funding databases — and cross-references for accuracy.</li>
              <li><strong>Confidence Scoring</strong> — Each enriched value receives a confidence score. Only high-confidence data (90%+) is written to your CRM; lower-confidence suggestions are flagged for review.</li>
              <li><strong>Continuous Freshness</strong> — The agent monitors for change signals (job changes, funding events, company news) and triggers re-enrichment proactively — not just at the point of initial import.</li>
            </ol>

            <h3 className="text-2xl font-bold text-foreground">CRM Enrichment Agent vs. Apollo vs. ZoomInfo</h3>
            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse bg-card rounded-xl overflow-hidden border border-border text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-3 font-semibold text-foreground">Feature</th>
                    <th className="p-3 font-semibold text-foreground text-center">Apollo / ZoomInfo</th>
                    <th className="p-3 font-semibold text-foreground text-center">NoQ5 Enrichment Agent</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t border-border"><td className="p-3">Data Model</td><td className="p-3 text-center">Static database</td><td className="p-3 text-center font-semibold text-foreground">Multi-source AI aggregation</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Freshness</td><td className="p-3 text-center">Point-in-time lookup</td><td className="p-3 text-center font-semibold text-foreground">Continuous re-enrichment</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Pricing</td><td className="p-3 text-center">$15K-50K/year (credits)</td><td className="p-3 text-center font-semibold text-foreground">Included in CRM agent</td></tr>
                  <tr className="border-t border-border"><td className="p-3">CRM Updates</td><td className="p-3 text-center">Contact data only</td><td className="p-3 text-center font-semibold text-foreground">Contacts + deals + activities</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Meeting Intelligence</td><td className="p-3 text-center">❌</td><td className="p-3 text-center font-semibold text-foreground">✅ Full conversation analysis</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Pipeline Forecasting</td><td className="p-3 text-center">❌</td><td className="p-3 text-center font-semibold text-foreground">✅ AI-powered predictions</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Multi-CRM</td><td className="p-3 text-center">⚠️ Limited</td><td className="p-3 text-center font-semibold text-foreground">✅ SF + HS + Pipedrive</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Fields Enriched */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            30+ Fields Enriched Automatically
          </h2>
          <p className="text-center text-muted-foreground mb-12">Every data point your sales process needs — sourced, verified, and written to your CRM without manual research.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {enrichmentFields.map((cat) => (
              <div key={cat.category} className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Database className="h-5 w-5 text-primary" />
                  {cat.category}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {cat.fields.map((field) => (
                    <span key={field} className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">{field}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Stop Paying for Static Enrichment Databases
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            NoQ5's CRM enrichment agent fills every field, refreshes continuously, and is included in your CRM agent — no credits, no separate contracts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial">
              <Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                Try Enrichment Agent Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/crm-agent">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">
                See Full CRM Agent
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">Related CRM Agent Resources</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent (Pillar Page)</Link>
            <Link to="/crm-data-entry-automation" className="text-primary hover:underline text-sm">CRM Data Entry Automation</Link>
            <Link to="/ai-agent-salesforce" className="text-primary hover:underline text-sm">AI Agent for Salesforce</Link>
            <Link to="/blog/crm-automation-guide" className="text-primary hover:underline text-sm">CRM Automation Guide</Link>
            <Link to="/ai-workflow-automation" className="text-primary hover:underline text-sm">AI Workflow Automation</Link>
            <Link to="/comparison" className="text-primary hover:underline text-sm">CRM Agent Comparisons</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CRMEnrichmentAgent;
